import { useCallback, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react'

import {
    THEME_STORAGE_KEY,
    ThemeContext,
    type ResolvedTheme,
    type Theme,
    type ThemeContextValue
} from '@/theme/theme-context'

const LIGHT_QUERY = '(prefers-color-scheme: light)'

function readStoredTheme(): Theme {
    try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY)
        if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
    } catch {
        // Private mode or storage disabled: fall through to the default.
    }
    // Dark is the product default, not a mirror of the OS. Operators asked for it,
    // and `system` remains available as an explicit choice.
    return 'dark'
}

function subscribeToSystemTheme(onChange: () => void) {
    const media = window.matchMedia(LIGHT_QUERY)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
}

function getSystemTheme(): ResolvedTheme {
    return window.matchMedia(LIGHT_QUERY).matches ? 'light' : 'dark'
}

function apply(resolved: ResolvedTheme) {
    const root = document.documentElement
    root.classList.toggle('dark', resolved === 'dark')
    root.style.colorScheme = resolved
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setThemeState] = useState<Theme>(readStoredTheme)

    // The OS preference is an external store, so subscribing to it directly keeps
    // `resolvedTheme` derived rather than mirrored into state -- and it only matters
    // while the operator's choice is `system`.
    const systemTheme = useSyncExternalStore(subscribeToSystemTheme, getSystemTheme, () => 'dark' as const)
    const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme

    const setTheme = useCallback((next: Theme) => {
        setThemeState(next)
        try {
            localStorage.setItem(THEME_STORAGE_KEY, next)
        } catch {
            // Preference simply will not persist; the session still honours it.
        }
    }, [])

    // The one genuinely external thing to synchronise: the class on <html>.
    useEffect(() => {
        apply(resolvedTheme)
    }, [resolvedTheme])

    // Staff routinely keep several admin tabs open; a toggle in one should not
    // leave the others disagreeing.
    useEffect(() => {
        const onStorage = (event: StorageEvent) => {
            if (event.key !== THEME_STORAGE_KEY) return
            setThemeState(readStoredTheme())
        }
        window.addEventListener('storage', onStorage)
        return () => window.removeEventListener('storage', onStorage)
    }, [])

    const value = useMemo<ThemeContextValue>(
        () => ({ theme, resolvedTheme, setTheme }),
        [theme, resolvedTheme, setTheme]
    )

    return <ThemeContext value={value}>{children}</ThemeContext>
}
